import { useCallback, useMemo } from 'react'

import {
  MemberPrivateMessagingSettings,
  PrivateMessagingIncomingContact,
} from '@tribeplatform/gql-client/types'
import { MessagingCustomHotkey } from '@tribeplatform/react-components/Composer/components'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useAuthMember, useUpdateMember } from '@tribeplatform/react-sdk/hooks'
import { useQueryClient } from '@tribeplatform/react-sdk/lib'
import {
  getSpaceKey,
  getSpacesKey,
  getAuthMemberKey,
} from '@tribeplatform/react-sdk/utils/keys'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Hotkey } from '@tribeplatform/react-ui-kit/Hotkey'
import { RadioGroup } from '@tribeplatform/react-ui-kit/RadioGroup'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

type FormFields = Pick<
  MemberPrivateMessagingSettings,
  'whoCanContactMember' | 'quickEnter'
>

export const MemberMessagingSettings = () => {
  const { $t } = useI18n()

  const { data: authMember } = useAuthMember()
  const { whoCanContactMember, quickEnter } =
    authMember?.settings?.privateMessaging || {}

  const queryClient = useQueryClient()

  const { mutateAsync: updateMember, isLoading } = useUpdateMember({
    useMutationOptions: {
      onSuccess: () => {
        queryClient.invalidateQueries(getAuthMemberKey())
        // now all spaces where member is a participant need to be refetched
        // to get updated `canSendPrivateMessage` permission
        queryClient.invalidateQueries(getSpacesKey())
        queryClient.invalidateQueries(getSpaceKey())
      },
    },
  })

  const onSubmit = useCallback(
    async (variables: FormFields) => {
      try {
        await updateMember({
          input: {
            settings: {
              privateMessaging: {
                whoCanContactMember: variables.whoCanContactMember,
                quickEnter: variables.quickEnter,
              },
            },
          },
        })
        toast({
          title: $t({
            defaultMessage: 'Your messaging settings are updated.',
            id: 'Messaging.Settings.Updated',
          }),
          status: 'success',
        })
      } catch (error) {
        toast({
          title: $t({
            defaultMessage: 'Your messaging settings cannot be updated.',
            id: 'Messaging.Settings.FailedToUpdate',
          }),
          description: error?.message,
          status: 'error',
        })
      }
    },
    [updateMember, $t],
  )

  const defaultValues = useMemo(
    () => ({
      whoCanContactMember,
      quickEnter: !!quickEnter,
    }),
    [whoCanContactMember, quickEnter],
  )

  return (
    <Card>
      <Card.Header
        title={$t({
          defaultMessage: 'Messaging preferences',
          id: 'Messaging.User.Settings.Title',
        })}
      />
      <Card.Content>
        <Form defaultValues={defaultValues} onSubmit={onSubmit}>
          {({ formState }) => (
            <Form.Layout className="flex flex-col gap-4">
              <Form.RadioGroup
                name="whoCanContactMember"
                label={$t({
                  defaultMessage:
                    'Choose who can send messages to you, all {MEMBERS} or only admins and moderators.',
                  id: 'Messaging.User.Settings.Description',
                })}
              >
                <RadioGroup.Items>
                  <RadioGroup.Item
                    value={PrivateMessagingIncomingContact.Anyone}
                    title={$t({
                      defaultMessage: 'Anyone can message you.',
                      id: 'Messaging.User.Settings.Anyone',
                    })}
                  />
                  <RadioGroup.Item
                    value={PrivateMessagingIncomingContact.Staff}
                    title={$t({
                      defaultMessage:
                        'Only admins and moderators can message you.',
                      id: 'Messaging.User.Settings.StaffOnly',
                    })}
                  />
                </RadioGroup.Items>
              </Form.RadioGroup>
              <Form.RadioGroup
                name="quickEnter"
                label={$t(
                  {
                    defaultMessage:
                      'When writing a message, press <enter_key>Enter</enter_key> to ...',
                    id: 'General.EnableNotifications',
                  },
                  {
                    enter_key: ([value]) => (
                      <MessagingCustomHotkey value={value} />
                    ),
                  },
                )}
              >
                <RadioGroup.Items>
                  <RadioGroup.Item
                    value
                    title={$t({
                      defaultMessage: 'Send the message',
                      id: 'Messaging.User.Settings.SendMessage',
                    })}
                  />
                  <RadioGroup.Item
                    value={false}
                    title={$t(
                      {
                        defaultMessage:
                          'Start a new line (use <shift_key>command,ctrl</shift_key> <enter_key>Enter</enter_key> to send)',
                        id: 'Messaging.User.Settings.NewLine',
                      },
                      {
                        shift_key: ([value]) => (
                          <Hotkey
                            className="bg-surface-subdued !text-md px-2 rounded-base-sm font-medium"
                            size="md"
                          >
                            {value}
                          </Hotkey>
                        ),
                        enter_key: ([value]) => (
                          <MessagingCustomHotkey value={value} />
                        ),
                      },
                    )}
                  />
                </RadioGroup.Items>
              </Form.RadioGroup>
              <Form.Actions>
                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={!formState.isDirty}
                >
                  <T defaultMessage="Update" id="Generics.Update" />
                </Button>
              </Form.Actions>
            </Form.Layout>
          )}
        </Form>
      </Card.Content>
    </Card>
  )
}
